import { Modal, Box, Typography, FormControl, InputLabel, Select, MenuItem, Button } from '@mui/material';
import { useState } from 'react';
import { FILE_TYPES, FileType, FileTypeKey } from '../../containers/FilesPage';
import { extractError } from '../../services/utils';
import AuthService from '../../services/authService';
import { toast } from 'react-toastify';
import UserService from '../../services/userService';
import { FileUploader } from 'react-drag-drop-files';
import { COLORS } from '../../styles/colors';

const UploadFileModal = ({ open, onClose, onUpload }: { open: boolean, onClose: () => void, onUpload: () => void }) => {
	const user = AuthService.getAuth()?.user;
	
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [selectedFileType, setSelectedFileType] = useState<FileType>(FILE_TYPES['Home Sleep Test Results']);
	const [loading, setLoading] = useState<boolean>(false);

	const handleFileInput = (file: File) => {
		setSelectedFile(file);
	};

	const handleUpload = async () => {
		if (!selectedFile || !user?.id) {
			toast.error('Please select a file to upload');
			return;
		}

		try {
			setLoading(true);

			// Create form data for file upload
			const formData = new FormData();
			formData.append('file', selectedFile, selectedFile.name);
			formData.append('keyPath', `patients/${user.id}/files/`);

			// Upload file to AWS
			const uploadResponse = await UserService.uploadFile(formData);
			const uploadedFileName = uploadResponse?.data?.file;

			if (!uploadedFileName) {
				throw new Error('File upload failed');
			}

			// Create form record
			await UserService.uploadForm({
				category: selectedFileType,
				name: uploadedFileName,
				key: `patients/${user.id}/files/${uploadedFileName}`,
				patientId: user.id,
				usedFor: 'Upload in sidebar',
				attachFile: true
			});

			toast.success('File uploaded successfully');
			setSelectedFile(null);
			setSelectedFileType(FILE_TYPES['Home Sleep Test Results']);
			onUpload();
			onClose();
		} catch (error) {
			toast.error(extractError(error, 'Failed to upload file'));
		} finally {
			setLoading(false);
		}
	};

	return (
		<Modal
			open={open}
			onClose={onClose}
			aria-labelledby="upload-modal-title"
			sx={{ borderRadius: '8px' }}
		>
			<Box sx={{
				position: 'absolute',
				top: '50%',
				left: '50%',
				transform: 'translate(-50%, -50%)',
				// width: 400,
				bgcolor: 'background.paper',
				boxShadow: 24,
				p: 4,
				borderRadius: 2,
			}}>
				<Typography id="upload-modal-title" variant="h6" component="h2" gutterBottom color={COLORS.DARK_GREY}>
					Upload File
				</Typography>

				<FormControl fullWidth sx={{ my: 2 }}>
					<InputLabel>File Type</InputLabel>
					<Select
						sx={{ borderRadius: '8px' }}
						value={selectedFileType}
						label="File Type"
						onChange={(e) => setSelectedFileType(e.target.value as FileType)}
					>
						{Object.keys(FILE_TYPES).map((type: string) => (
							<MenuItem key={type} value={FILE_TYPES[type as FileTypeKey]}>{type}</MenuItem>
						))}
					</Select>
				</FormControl>

				<Box>
					<FileUploader
						handleChange={handleFileInput}
						children={
							<Box
								sx={{
									width: '336px',
									border: '2px dashed',
									borderColor: 'grey.300',
									borderRadius: '8px',
									p: 3,
									textAlign: 'center',
									cursor: 'pointer',
									'&:hover': {
										borderColor: 'primary.main',
									},
								}}
							>
								<Typography color={COLORS.DARK_GREY}>
									{selectedFile ? selectedFile.name : 'Drag & Drop or click to select a file'}
								</Typography>
							</Box>
						}
						name="file"
						types={['PDF']}
					/>
				</Box>

				<Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
					<Button 
						onClick={onClose}
						variant="outlined"
					>
						Cancel
					</Button>
					<Button 
						onClick={handleUpload}
						variant="contained"
						disabled={!selectedFile || loading}
					>
						{loading ? 'Uploading...' : 'Upload'}
					</Button>
				</Box>
			</Box>
		</Modal>
	)
}

export default UploadFileModal;